/**
 * The shared vocabulary of the findings redesign (phase 1).
 *
 * - A **finding** is a compliance gap. Besides regulatory findings, two
 *   suggestion kinds are gaps too: a control amendment (the document does not
 *   meet a mapped control) and a missing document. They join the findings
 *   queue; everything else a suggestion can say is a **record update**.
 * - Planning is not a status: a finding carries an assignee and a due date
 *   beside its status.
 * - A dismissal carries a reason category as well as a note.
 *
 * Schema-only plus pure helpers, so the API, the database layer and the web
 * client read one definition.
 *
 * @module platform-shared/findingPlanning
 */
import * as Schema from "effect/Schema";

/** Why a finding was dismissed. Stored as these keys; labels are for people. */
export const DISMISS_CATEGORIES = [
  "not_applicable",
  "already_covered",
  "risk_accepted",
  "duplicate",
] as const;
export type DismissCategory = (typeof DISMISS_CATEGORIES)[number];

export const DISMISS_CATEGORY_LABELS: Readonly<Record<DismissCategory, string>> = {
  not_applicable: "Not applicable",
  already_covered: "Already covered",
  risk_accepted: "Risk accepted",
  duplicate: "Duplicate",
};

export const isDismissCategory = (value: unknown): value is DismissCategory =>
  typeof value === "string" && (DISMISS_CATEGORIES as ReadonlyArray<string>).includes(value);

/** A stored category, or null for rows written before categories existed. */
export const readDismissCategory = (value: unknown): DismissCategory | null =>
  isDismissCategory(value) ? value : null;

/** Suggestion kinds that are compliance gaps, so they are findings. */
export const GAP_SUGGESTION_KINDS = ["control_amendment", "missing_document"] as const;
export type GapSuggestionKind = (typeof GAP_SUGGESTION_KINDS)[number];

export const isGapSuggestionKind = (kind: string): kind is GapSuggestionKind =>
  (GAP_SUGGESTION_KINDS as ReadonlyArray<string>).includes(kind);

/** Every other suggestion kind is housekeeping: a record update. */
export const isRecordUpdateKind = (kind: string): boolean => !isGapSuggestionKind(kind);

/** `YYYY-MM-DD`: a due date is a day, not an instant. */
export const DueDate = Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/));

/**
 * Who works on a finding and by when. Both nullable: null clears. Absent
 * keys leave the stored value as it is, so a picker that changes one never
 * clears the other.
 */
export const FindingPlan = Schema.Struct({
  assigneeId: Schema.optional(Schema.NullOr(Schema.String)),
  dueDate: Schema.optional(Schema.NullOr(DueDate)),
});
export type FindingPlan = typeof FindingPlan.Type;

/** The body that plans a finding or a gap suggestion. */
export const FindingPlanPayload = Schema.Struct({ plan: FindingPlan });
export type FindingPlanPayload = typeof FindingPlanPayload.Type;

/** A due date string as a UTC midnight, or null for an invalid day. */
export function dueDateToTimestamp(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? null : date;
}

/** The stored instant back to its day. */
export const timestampToDueDate = (value: Date | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

/** A plan payload has to change something. */
export const planIsEmpty = (plan: FindingPlan): boolean =>
  plan.assigneeId === undefined && plan.dueDate === undefined;
